import { MANAGE_SECURITY, MANAGE_USERS } from '@rsos/constants/pageAccess';
import { fetchPsap } from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';
import {
  fetchUser,
  fetchAppCIDRs,
  fetchAppUsers,
  fetchAppRoles,
  fetchAllPermissions,
  setTokens,
  postAuthTokenSuccess,
} from '@rsos/sinatra';
import { getUrlParameter } from '@rsos/utils';
import { configureUserScope } from '@rsos/utils/sentry';
import {
  APP_INIT_START,
  APP_INIT_SUCCESS,
  APP_INIT_FAILED,
} from './actionTypes';

export const appInitStart = () => ({
  type: APP_INIT_START,
});

export const appInitSuccess = () => ({
  type: APP_INIT_SUCCESS,
});

export const appInitFailed = error => ({
  type: APP_INIT_FAILED,
  error,
});

const SESSION_EXPIRED_QUERY_PARAM = '?session_expired=true';

const hardRedirect = to => {
  window.location = to;
};

// Data loading only. Whether the user may reach a sub-page is decided by that
// route's <RequireAccess> guard in app/Main.tsx; the catch below handles
// authentication (no session / expired refresh token) rather than authorization.
//
// The permission checks that remain choose *which* fetches to make, because the
// endpoints themselves are permission-scoped: requesting CIDRs without
// MANAGE_SECURITY would 403 and fail initialization for a user who is only here
// for the Users pages.
const initialize = () => (dispatch, store) => {
  dispatch(appInitStart());
  const isLoggedIn = store().sinatra.user.isLoggedIn;

  const accessToken = getUrlParameter('access_token');
  if (accessToken) {
    const tokens = {
      refresh_token: null,
      token: accessToken,
    };
    setTokens(tokens);
    dispatch(postAuthTokenSuccess(tokens));
  }

  return dispatch(fetchUser())
    .then(user => {
      if (user && user.profile) {
        configureUserScope(user.profile);
      }
      const { sinatra } = store();
      const currentApp = sinatra.user.currentRole?.application;
      const org = sinatra.user.currentOrg;
      const permissions = sinatra.user.currentRole?.permissions || [];
      const canManageSecurity = permissions.includes(MANAGE_SECURITY);
      const canManageUsers = permissions.includes(MANAGE_USERS);

      if (!org?.name) {
        throw new Error('No admin organization for the current role');
      }

      // Await these so the app's initialized gate only opens once the
      // management data exists. selectManagementByApp returns undefined until
      // these *Success actions populate sinatra.management, so opening the gate
      // earlier renders components (UsersManagement, RolesManagement,
      // IPSecurity*) against undefined management and crashes.
      //
      // The PSAP is not one of them — /psaps/{name} is not permission-scoped, and
      // every other section fetches it unconditionally. It has to be
      // unconditional here too: the Manage SSO route is gated on an org
      // capability that comes from this PSAP, so fetching it only for
      // MANAGE_USERS holders locks a MANAGE_SSO-only admin out of the one page
      // their permission is for.
      const fetches = [dispatch(fetchPsap(`/psaps/${org.name}`)).unwrap()];
      if (canManageSecurity) {
        fetches.push(dispatch(fetchAppCIDRs(org.id, currentApp)));
      }
      if (canManageUsers) {
        fetches.push(
          dispatch(fetchAppUsers(org.id, currentApp)),
          dispatch(fetchAppRoles(org.id, currentApp)),
          dispatch(fetchAllPermissions(org.id, currentApp)),
        );
      }

      return Promise.all(fetches);
    })
    .then(() => dispatch(appInitSuccess()))
    .catch(action => {
      const isRefreshTokenExpired = Boolean(action?.isRefreshTokenExpired);

      // Only redirect with session-expired UX when refresh-token renewal failed.
      if (isLoggedIn && isRefreshTokenExpired) {
        return hardRedirect(`/${SESSION_EXPIRED_QUERY_PARAM}`);
      }

      if (isLoggedIn) {
        if (action.error) {
          return dispatch(appInitFailed(action.error));
        }
        return dispatch(appInitFailed(action));
      }

      return hardRedirect('/');
    });
};

export default initialize;
